// src/components/tasks/TaskList.jsx
import { useState, useEffect, useRef, useMemo } from "react";
import { EditTaskModal } from "./EditTaskModal";
import Button from "../ui/Button";
import { useBulkUpdateTasks, useDeleteTask } from "../../hooks/useTaskApi";
import { SquarePen, Trash2 } from "lucide-react";
import Swal from "sweetalert2";
import { useAuth } from "../../hooks/AuthContext";
import Pagination from "../Models/Pagination";
import DescriptionCell from "../Models/DescriptionCell";

// --- Compute Effective Status ---
const getEffectiveStatus = (task) => {
  if (task.status === "completed") return "completed";
  if (task.status === "to-do") return "to-do";
  if (task.status === "pending") return "pending";
  if (task.status === "hold") return "hold";
  if (task.status === "in-progress") return "in-progress";

  // if (new Date() > new Date(task.deadline)) return "Overdue";
  // return "Pending";
};

// --- Status Badge Colors ---
const getStatusBadge = (status) => {
  switch (status) {
    case "to-do":
      return "bg-red-200 text-red-800";
    case "pending":
      return "bg-yellow-200 text-yellow-800";
    case "hold":
      return "bg-gray-300 text-gray-800";
    case "in-progress":
      return "bg-blue-300 text-gray-800";
    case "completed":
      return "bg-green-200 text-green-800";
    default:
      return "bg-gray-200 text-gray-800";
  }
};


// --- Priority Badge Colors ---
const getPriorityBadge = (priority) => {
  switch (priority) {
    case "low":
      return "text-blue-800";
    case "medium":
      return "text-yellow-800";
    case "high":
      return "text-orange-800";
    case "urgent":
      return "text-red-800";
    default:
      return "text-gray-800";
  }
};

export const TaskList = ({
  tasks,
  isHrView = false,
  statusFilter = [],
  editable = true,
  onPageChange = () => { },
  paginationData = {},

}) => {


  const [editingTask, setEditingTask] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  const { mutate: bulkUpdate, isLoading: isBulkUpdating } = useBulkUpdateTasks();
  const selectAllCheckboxRef = useRef();
  const { mutate: deleteTask } = useDeleteTask();
  const [taskList, setTaskList] = useState(tasks);

  const { user } = useAuth();

  useEffect(() => {
    setTaskList(tasks); // update when parent sends new tasks
  }, [tasks]);
  // 🔹 Filter tasks based on effectiveStatus
  const filteredTasks = useMemo(() => {
    return (
      taskList?.filter((task) => {
        const effectiveStatus = getEffectiveStatus(task);
        if (!statusFilter || statusFilter.length === 0) return true;
        return statusFilter.includes(effectiveStatus);
      }) || []
    );
  }, [taskList, statusFilter]);


  // 🔹 Handle "Select All" checkbox
  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      const allVisibleIds = filteredTasks.map((t) => t._id);
      const allSelected =
        allVisibleIds.length > 0 &&
        allVisibleIds.every((id) => selectedTaskIds.has(id));
      selectAllCheckboxRef.current.checked = allSelected;
      selectAllCheckboxRef.current.indeterminate =
        selectedTaskIds.size > 0 && !allSelected;
    }
  }, [selectedTaskIds, filteredTasks]);

  const handleSelectAll = (e) => {
    setSelectedTaskIds(
      e.target.checked ? new Set(filteredTasks.map((t) => t._id)) : new Set()
    );
  };

  useEffect(() => {
    if (editingTask) {
      // Disable background scroll when modal opens
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scroll when modal closes
      document.body.style.overflow = "auto";
    }

    // Cleanup on unmount just in case
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [editingTask]);

  const handleSelectRow = (taskId) => {
    const newSelection = new Set(selectedTaskIds);
    newSelection.has(taskId) ? newSelection.delete(taskId) : newSelection.add(taskId);
    setSelectedTaskIds(newSelection);
  };

  const handleBulkComplete = () => {
    if (selectedTaskIds.size === 0) return;
    bulkUpdate(
      { taskIds: Array.from(selectedTaskIds), status: "completed", updatedBy: user?.employeeProfile?.id },
      {
        onSuccess: () => {
          Swal.fire({
            title: "Success",
            text: "Tasks marked as completed",
            icon: "success",
            timer: 2000,
            // showConfirmButton: false, // ❌ no "OK" button
            timerProgressBar: true,   // optional: adds a progress bar
          });

          setSelectedTaskIds(new Set());
        },
        onError: (error) =>
          Swal.fire("Error", error.response?.data?.message || error.message, "error"),
      }
    );
  };


  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete this task?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    }).then((res) => {
      if (res.isConfirmed) {
        deleteTask(id, {
          onSuccess: () => {
            Swal.fire({
              title: "Deleted!",
              icon: "success",
              timer: 1000,
              showConfirmButton: false,
            });
            // Update local state
            setTaskList((prev) => prev.filter((t) => t._id !== id));
          },
        });
      }
    });
  };

  if (!filteredTasks.length) {
    return <p className="text-gray-500 mt-4">No tasks found.</p>;
  }


  return (
    <div className="p-4 bg-white rounded-lg flex flex-col gap-4">
      <div className="overflow-x-auto rounded-lg shadow border">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {isHrView && (
                <th className="py-3">
                  <input
                    type="checkbox"
                    ref={selectAllCheckboxRef}
                    onChange={handleSelectAll}
                  />
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Task
              </th>

              {!isHrView && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
              )}

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Assigned By
              </th>
              {isHrView && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Assigne To
                </th>
              )}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Deadline
              </th>

              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Action
              </th>

            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTasks.map((task) => {
              const effectiveStatus = getEffectiveStatus(task);
              return (
                <tr key={task?._id} className="hover:bg-gray-50">
                  {isHrView && (
                    <td className="px-2 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTaskIds.has(task?._id)}
                        onChange={() => handleSelectRow(task?._id)}
                      />
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{task?.title}</div>

                    <div className="text-xs text-gray-500">
                      Priority:{" "}
                      <span className={getPriorityBadge(task?.priority)}>
                        {task?.priority}
                      </span>
                    </div>
                  </td>
                  {!isHrView && (
                    <DescriptionCell description={task?.description} />
                  )}

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {task?.assignedBy?.name || "N/A"}
                  </td>
                  {isHrView && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {task?.assignedTo?.name || "N/A"}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 inline-flex text-xs font-semibold rounded-full ${getStatusBadge(
                        effectiveStatus
                      )}`}
                    >
                      {effectiveStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(task?.endDate * 1000).toLocaleDateString()}
                  </td>


                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-4">
                      <button
                        onClick={() => setEditingTask(task)}
                        className="text-green-500 hover:text-green-600"
                      >
                        <SquarePen />
                      </button>
                      {/* ✅ Only show delete button if user is NOT an employee */}
                      {user?.role !== "employee" && (
                        <button
                          onClick={() => handleDelete(task?._id)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 />
                        </button>
                      )}
                    </div>

                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EditTaskModal task={editingTask} onClose={() => setEditingTask(null)} />

      {isHrView && (
        <div className="mt-4 flex items-center space-x-4">
          <Button
            variant="primary"
            onClick={handleBulkComplete}
            disabled={selectedTaskIds.size === 0 || isBulkUpdating}
          >
            Mark Selected ({selectedTaskIds.size}) as Completed
          </Button>
        </div>
      )}
      <Pagination
        currentPage={paginationData?.page || 1}
        totalPages={paginationData?.pages || 1}
        onPageChange={onPageChange}
        maxVisible={3}
      />

    </div>
  );
};
